import React, { useLayoutEffect, useRef, useState } from 'react';
import { Size } from '@philips-internal/oswald-ui-constants';
import { AbortableDismissEvent, ClickEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, SessionStorageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, createAbort, isEnumValid, isFunction, isXSmallResolution } from '@philips-internal/oswald-ui-utils';
import { Link } from '../link/link.js';
import { Token } from '../token/token.js';
import { TokenSize } from './token-list-constants.js';
import { TokenListProperties } from './token-list-types.js';
import './token-list.css';
function getPixelsFromPropertyValue(element, property) {
    const propertyValue = getComputedStyle(element).getPropertyValue(property);
    const cssValuePattern = /^([-.\d]+(?:\.\d+)?)(.*)$/;
    const match = propertyValue.trim().match(cssValuePattern);
    if (!match) {
        return 0;
    }
    const value = Number(match[1]);
    const unit = match[2].trim();
    if (unit === 'px') {
        return value;
    }
    const documentFontSize = Number(getComputedStyle(document.documentElement).fontSize.match(cssValuePattern)?.[1]);
    return value * documentFontSize;
}
function TokenListToggleLink(props) {
    if (!props.collapsed) {
        return null;
    }
    const size = props.size === TokenSize.NORMAL
        ? Size.SMALL
        : Size.LARGE;
    const expression = props.showHiddenTokens
        ? 'tws.token-list.hide'
        : MessageService.translate('tws.token-list.show', { hiddenAmount: props.hiddenAmount });
    return (React.createElement(Link, { onClick: props.onClick, size: size, value: expression }));
}
const TOKEN_LIST_TOGGLE_LINK_SIZE_KEY = 'oswald:token-list:toggle-width';
/**
 * Calculates the width of the toggle link element within the given container, storing the result
 * in the browser's session storage to prevent unnecessary appends in future renders.
 *
 * @param container - The container element where the toggle link will be appended.
 */
function getToggleLinkWidth(container) {
    const existingWidth = SessionStorageService.getItem(TOKEN_LIST_TOGGLE_LINK_SIZE_KEY);
    if (existingWidth) {
        return existingWidth;
    }
    const element = document.createElement('div');
    element.className = 'c-token-list__action-button c-pre-render';
    element.innerText = MessageService.translate('tws.token-list.show', { hiddenAmount: 10 });
    container.appendChild(element);
    const elementWidth = element.getBoundingClientRect().width;
    container.removeChild(element);
    SessionStorageService.setItem(TOKEN_LIST_TOGGLE_LINK_SIZE_KEY, elementWidth);
    return elementWidth;
}
/**
 * Marks hidden tokens in the container when the tokens exceed the container width.
 *
 * @param container - The container element containing tokens.
 */
function markHiddenTokens(container) {
    const tokens = container.querySelectorAll('.c-token');
    const linkWidth = getToggleLinkWidth(container);
    const maxWidth = container.getBoundingClientRect().width - linkWidth;
    const gap = getPixelsFromPropertyValue(container, '--tws-token-list-horizontal-gap');
    let currentWidth = 0;
    let widthExceeded = false;
    let exceedingTokens = 0;
    for (const token of tokens) {
        if (!widthExceeded) {
            const tokenWidth = token.getBoundingClientRect().width;
            currentWidth += tokenWidth;
            if (currentWidth <= maxWidth) {
                currentWidth += gap;
                continue;
            }
            else {
                widthExceeded = true;
            }
        }
        if (widthExceeded) {
            token.setAttribute('data-hidden', 'true');
            exceedingTokens += 1;
        }
    }
    return {
        exceedingTokens,
    };
}
/**
 * Clears the 'data-hidden' attribute from tokens in the container.
 *
 * @param container - The container element containing tokens.
 */
function clearTokens(container) {
    const tokens = container.querySelectorAll('.c-token[data-hidden]');
    for (const token of tokens) {
        token.removeAttribute('data-hidden');
    }
}
/**
 * Toggles the visibility of hidden tokens in the container by changing the 'data-hidden' attribute.
 *
 * @param container - The container element containing tokens to toggle.
 */
function toggleTokens(container) {
    const tokens = container.querySelectorAll('.c-token[data-hidden]');
    for (const token of tokens) {
        const isHidden = token.getAttribute('data-hidden') === 'true';
        token.setAttribute('data-hidden', String(!isHidden));
    }
}
function TokenList(props) {
    const { collapsed = TokenListProperties.collapsed.defaultValue, data = TokenListProperties.data.defaultValue, justifyContent = TokenListProperties.justifyContent.defaultValue, noWrap = TokenListProperties.noWrap.defaultValue, shape = TokenListProperties.shape.defaultValue, size = TokenListProperties.size.defaultValue, } = props;
    const containerRef = useRef(null);
    const [hiddenTokensAmount, setHiddenTokensAmount] = useState(0);
    const [showHiddenTokens, setShowHiddenTokens] = useState(false);
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || !data.length) {
            return;
        }
        clearTokens(container);
        if (!collapsed) {
            setHiddenTokensAmount(0);
            return;
        }
        const { exceedingTokens } = markHiddenTokens(container);
        setHiddenTokensAmount(exceedingTokens);
        setShowHiddenTokens(false);
    }, [collapsed, data, size, props.squeezed]);
    const onClickEvent = (callback, event) => {
        if (callback) {
            callback(new ClickEvent(event));
        }
    };
    const onDismiss = (event) => {
        const abort = createAbort();
        if (isFunction(props.onBeforeDismiss)) {
            props.onBeforeDismiss(new AbortableDismissEvent({ abort }));
        }
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            if (isFunction(props.onDismiss)) {
                props.onDismiss(event);
            }
        });
    };
    const onToggleClick = () => {
        const wrapperElement = containerRef.current;
        if (!wrapperElement) {
            return;
        }
        toggleTokens(wrapperElement);
        setShowHiddenTokens(previousShowHiddenTokens => !previousShowHiddenTokens);
    };
    if (!data.length) {
        return null;
    }
    const className = classNames({
        'c-token-list': true,
        'c-token-list--collapsed': Boolean(collapsed),
        'c-token-list--no-wrap': isXSmallResolution() && Boolean(noWrap),
        'c-token-list--squeezed': Boolean(props.squeezed),
        [`c-token-list--${size}`]: isEnumValid(TokenSize, size),
        [`${props.className}`]: Boolean(props.className),
    });
    const style = {
        justifyContent,
    };
    return (React.createElement("div", { className: className, ref: containerRef, style: style },
        data.map((item, index) => {
            return (React.createElement(Token, { ...item, key: index, onClick: (event) => onClickEvent(props.onClick, event), onContextMenuClick: (event) => onClickEvent(props.onContextMenuClick, event), onDismiss: onDismiss, onDoubleClick: (event) => onClickEvent(props.onDoubleClick, event), shape: shape, size: size }));
        }),
        hiddenTokensAmount > 0 ? (React.createElement(TokenListToggleLink, { collapsed: collapsed, hiddenAmount: hiddenTokensAmount, onClick: onToggleClick, showHiddenTokens: showHiddenTokens, size: size })) : null));
}
export { TokenList, };
//# sourceMappingURL=token-list.js.map